import { useState, useCallback } from 'react';
import { paymentService } from '../services/newApi';
import { toast } from 'sonner';

const useNewRazorpay = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  const initiatePayment = useCallback(async (customerDetails, purchaseInfo) => {
    setIsProcessing(true);
    setPaymentStatus(null);

    try {
      // Prepare order data
      const orderData = {
        customer_details: {
          name: customerDetails.name,
          email: customerDetails.email,
          mobile_number: customerDetails.phone
        },
        purchase_items: {
          book_ids: purchaseInfo.bookIds,
          purchase_type: purchaseInfo.type,
          total_amount: purchaseInfo.amount
        }
      };

      // Create payment order
      const order = await paymentService.createOrder(orderData);
      
      // Check if Razorpay is available
      if (!window.Razorpay) {
        throw new Error('Razorpay not loaded. Please refresh the page.');
      }

      // Configure Razorpay options
      const options = {
        key: order.key_id,
        amount: order.amount * 100, // Convert to paise
        currency: order.currency,
        name: 'Marriage 101 Series',
        description: `${purchaseInfo.type === 'bundle' ? 'Complete Series' : 'Individual Book'}`,
        image: `${window.location.origin}/logo192.png`,
        order_id: order.id,
        prefill: {
          name: customerDetails.name,
          email: customerDetails.email,
          contact: customerDetails.phone
        },
        theme: {
          color: '#2563eb' // Blue color matching new theme
        },
        modal: {
          ondismiss: () => {
            setIsProcessing(false);
            toast.error('Payment cancelled');
          }
        },
        handler: async (response) => {
          try {
            // Verify payment
            const verificationData = {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              customer_details: {
                name: customerDetails.name,
                email: customerDetails.email,
                mobile_number: customerDetails.phone
              }
            };

            const verification = await paymentService.verifyPayment(verificationData);

            if (verification.success) {
              setPaymentStatus('success');
              
              toast.success('Payment successful! 🎉', {
                description: 'Redirecting to download page...',
                duration: 3000,
              });

              // Redirect to download page
              setTimeout(() => {
                const downloadUrl = verification.data.download_url;
                window.location.href = downloadUrl;
              }, 2000);

            } else {
              throw new Error('Payment verification failed');
            }
            
          } catch (error) {
            console.error('Payment verification error:', error);
            setPaymentStatus('failed');
            toast.error('Payment verification failed', {
              description: 'Please contact support if money was deducted.',
            });
          }
        }
      };

      // Open Razorpay checkout
      const razorpay = new window.Razorpay(options);
      razorpay.open();

      return true;

    } catch (error) {
      console.error('Payment initiation error:', error);
      setPaymentStatus('failed');
      toast.error('Failed to initiate payment', {
        description: error.message || 'Please try again or contact support.',
      });
      
      return false;
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const resetPaymentStatus = useCallback(() => {
    setPaymentStatus(null);
  }, []);

  return {
    initiatePayment,
    resetPaymentStatus,
    isProcessing,
    paymentStatus
  };
};

export default useNewRazorpay;